import { TableToolbar, TableToolbarContent } from "@carbon/react";
import { NullableTableColumns } from "@flaminem/ui-components";
import { FC, useMemo } from "react";

import { DimensionedLayout } from "src/components/DimensionedLayout/DimensionedLayout.tsx";
import { useDimensions } from "src/components/DimensionedLayout/useDimensions.ts";
import { FixedTable } from "src/components/Tables/FixedTable";
import { TableToolbarSearch } from "src/components/Tables/TableToolbarSearch";
import { defaultRowHeight } from "src/config/table-config";
import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider";
import { HeadlessWidgetRenderer } from "src/dynamic-view-renderer/headlessWidgets/HeadlessWidgetRenderer";
import { useLocalSearchByFields } from "src/hooks/useLocalSearch";
import { useT } from "src/internationalization/useT";
import { LinkedCase } from "src/types/case";

export type RelatedCasesTableProps = {
  cases: LinkedCase[];
  loading: boolean;
  isError: boolean;
  caseTypeCode?: string;
  /**
   * If true, uses full-height layout (for full-page tables).
   * If false, uses flexible height (for modals/constrained containers).
   * @default true
   */
  fullHeight?: boolean;
};

export const RelatedCasesTable: FC<RelatedCasesTableProps> = ({
  cases,
  isError,
  loading,
  caseTypeCode,
  fullHeight = true,
}) => {
  const t = useT();
  const { caseTypeDefinitions, headlessWidgets } = useDynamicViewSchema();
  const { pageSize, dimensions } = useDimensions();

  // Get caseTypeCode from first case if not provided
  const resolvedCaseTypeCode = caseTypeCode ?? cases[0]?.typeCode;

  const columns: NullableTableColumns<LinkedCase> = useMemo(() => {
    // Find the case type configuration
    const caseTypeConfig = resolvedCaseTypeCode
      ? caseTypeDefinitions?.find((ct) => ct.code === resolvedCaseTypeCode)
      : undefined;

    const caseListTable = caseTypeConfig?.caseListTable;

    if (!caseListTable) {
      return {};
    }

    const dynamicColumns = caseListTable.columns.reduce((acc, column) => {
      const widget = headlessWidgets.find((w) => w.code === column.headlessWidgetCode);
      if (!widget) return acc;

      return {
        ...acc,
        [column.headlessWidgetCode]: {
          label: column.headerLabel,
          flexGrow: column.flexGrow ?? (column.width && column.width !== 1 ? 0 : 1),
          flexShrink: column.flexShrink ?? (column.width && column.width !== 1 ? 0 : 1),
          width: column.width || 1,
          className: "text-sm",
          render: (row: LinkedCase) => (
            <HeadlessWidgetRenderer
              widget={widget}
              data={{
                root: row,
                elements: row.elements,
                propertyValues: row.properties,
                caseTypeDefinitions: caseTypeDefinitions,
              }}
            />
          ),
          sorter: column.sorter,
        },
      };
    }, {});

    return dynamicColumns;
  }, [resolvedCaseTypeCode, caseTypeDefinitions, headlessWidgets]);

  // Get filter fields from column labels for search placeholder
  const filterFields = useMemo(() => {
    return Object.values(columns)
      .filter((col): col is NonNullable<typeof col> => col !== null && col !== undefined)
      .map((col) => col.label as string);
  }, [columns]);

  const { searchTerm, filter, handleSearch } = useLocalSearchByFields<LinkedCase>([
    "id",
    "request",
    "typeCode",
  ]);

  // For modal context, use flex to fill available space
  const modalHeightClass = fullHeight ? undefined : "flex-1 min-h-0";

  return (
    <DimensionedLayout
      fullHeight={fullHeight}
      className={fullHeight ? undefined : modalHeightClass}
    >
      {dimensions?.width && (
        <FixedTable
          id="related-cases-table"
          title={t("cases.relatedCases.table.title")}
          description={t("cases.relatedCases.table.description")}
          data={cases}
          filter={filter}
          columns={columns}
          pageSize={pageSize}
          rowHeight={defaultRowHeight}
          isLoading={loading}
          isError={isError}
          noRowsText={t("general.table.empty")}
          toolbar={() => (
            <TableToolbar aria-label="data table toolbar">
              <TableToolbarContent>
                <TableToolbarSearch
                  search={searchTerm}
                  placeholder={t("cases.relatedCases.table.search.placeholder", {
                    fields: filterFields.join(", "),
                  })}
                  onSearchChange={handleSearch}
                />
              </TableToolbarContent>
            </TableToolbar>
          )}
        />
      )}
    </DimensionedLayout>
  );
};
